// Attendance > Schedule (Mercedes, Nov 3, 2026). Managers build their team's week here
// instead of on the board by F02.
//  * Customer Relations swaps between two shifts week to week (9:00 to 5:30 and 8:30 to
//    5:00): one click per person, or copy last week with the shifts swapped.
//  * Operations: a week grid with "copy last week" for the summer schedules where people
//    are off on set days.
//  * Getting it to the team: Print (a clean page for the board) and Share picture (a
//    phone-friendly image through the phone's share menu, for the team's group text;
//    most of the crew has no CPx login, so a link would not do).
// The server checks that each row is for a team this person builds (Rachael: CR;
// Carman, Brenda, Montel, Matt Jr: Operations).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendance } from '../../services/api';
import { Msg, box, btn, btn2, input, th, td, left, muted, addDays, monday, fmtDow, fmtMd, fmtDay, clockShort, todayEt } from './shared';

const CR_SHIFTS = [['09:00', '17:30'], ['08:30', '17:00']];
const key = (k, d) => `${k}|${d}`;

export default function Schedule({ me }) {
  const [mon, setMon] = useState(monday(addDays(todayEt(), 3)));
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const days = useMemo(() => [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(mon, i)), [mon]);

  const load = useCallback(async () => {
    setErr(''); setOk('');
    try {
      const r = await attendance('schedule-week', { monday: mon });
      setData(r);
      const m = {};
      for (const x of r.rows) m[key(x.person_key, x.day)] = { start_time: x.start_time || '', end_time: x.end_time || '', off: !!x.off };
      setRows(m); setDirty(false);
    } catch (e) { setErr(e.message); }
  }, [mon]);
  useEffect(() => { load(); }, [load]);

  const canEdit = (team) => data && data.canEdit.includes(team);
  const set = (k, d, v) => { setRows((r) => ({ ...r, [key(k, d)]: { ...(r[key(k, d)] || {}), ...v } })); setDirty(true); };
  const clear = (k, d) => { setRows((r) => ({ ...r, [key(k, d)]: { start_time: '', end_time: '', off: false } })); setDirty(true); };

  // Copy last week into this one, for one team. CR can swap the two shifts on the way.
  const copyLast = (team, swap) => {
    const next = { ...rows };
    for (const p of data.people.filter((x) => x.team === team)) {
      for (const d of days) {
        const prev = rows[key(p.key, addDays(d, -7))];
        if (!prev) { next[key(p.key, d)] = { start_time: '', end_time: '', off: false }; continue; }
        let v = { ...prev };
        if (swap && !v.off) {
          const i = CR_SHIFTS.findIndex(([a, b]) => a === v.start_time && b === v.end_time);
          if (i >= 0) { const [a, b] = CR_SHIFTS[1 - i]; v = { ...v, start_time: a, end_time: b }; }
        }
        next[key(p.key, d)] = v;
      }
    }
    setRows(next); setDirty(true);
  };
  const crShift = (p, i) => {
    const next = { ...rows };
    for (const d of days.slice(0, 5)) next[key(p.key, d)] = { start_time: CR_SHIFTS[i][0], end_time: CR_SHIFTS[i][1], off: false };
    setRows(next); setDirty(true);
  };

  const save = async () => {
    setBusy(true); setErr(''); setOk('');
    const out = [];
    for (const p of data.people.filter((x) => canEdit(x.team))) {
      for (const d of days) {
        const v = rows[key(p.key, d)] || { start_time: '', end_time: '', off: false };
        out.push({ person_key: p.key, day: d, start_time: v.start_time || null, end_time: v.end_time || null, off: !!v.off });
      }
    }
    try { await attendance('schedule-save', { rows: out }); setOk('Saved.'); setDirty(false); } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  if (!data) return <><Msg err={err} /><p>Loading the schedule…</p></>;
  const teams = [...new Set(data.people.map((p) => p.team))];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button type="button" style={btn2} onClick={() => setMon(addDays(mon, -7))}>Back</button>
        <button type="button" style={btn2} onClick={() => setMon(monday(todayEt()))}>This week</button>
        <button type="button" style={btn2} onClick={() => setMon(addDays(mon, 7))}>Next</button>
        <b>Week of {fmtDay(mon)}</b>
        <span style={{ flex: 1 }} />
        <button type="button" style={btn} disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      <Msg err={err} ok={ok} />
      {dirty && <p style={{ ...muted, color: '#8a5a00' }}>Not saved yet.</p>}

      {teams.map((t) => {
        const people = data.people.filter((p) => p.team === t);
        const editable = canEdit(t);
        return (
          <div key={t} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{me.teams[t]}</h3>
              {editable && <button type="button" style={btn2} onClick={() => copyLast(t, false)}>Copy last week</button>}
              {editable && t === 'cr' && <button type="button" style={btn2} onClick={() => copyLast(t, true)}>Copy last week, shifts swapped</button>}
              <button type="button" style={btn2} onClick={() => printWeek(me.teams[t], people, days, rows, data.holidays)}>Print</button>
              <button type="button" style={btn2} onClick={() => shareWeek(me.teams[t], people, days, rows, data.holidays).catch((e) => setErr(e.message))}>Share picture</button>
            </div>
            <div style={box}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={{ ...th, ...left, minWidth: 160 }}>Person</th>
                  {days.map((d) => <th key={d} style={{ ...th, textAlign: 'center', color: data.holidays.includes(d) ? '#b42318' : th.color }}>{fmtDow(d)} {fmtMd(d)}</th>)}
                  {editable && t === 'cr' && <th style={th}>Shift</th>}
                </tr></thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.key} style={{ borderTop: '1px solid #eef0f2' }}>
                      <td style={{ ...td, ...left, fontWeight: 600 }}>{p.name}{p.seasonal ? <span style={{ fontWeight: 400, color: '#6b7280' }}> (seasonal)</span> : null}</td>
                      {days.map((d) => {
                        const v = rows[key(p.key, d)] || {};
                        return (
                          <td key={d} style={{ ...td, textAlign: 'center', padding: '4px 3px', background: v.off ? '#f3f4f6' : undefined }}>
                            {editable ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                {v.off ? <span style={{ fontWeight: 700, color: '#6b7280' }}>OFF</span> : (
                                  <>
                                    <input type="time" value={v.start_time || ''} onChange={(e) => set(p.key, d, { start_time: e.target.value, off: false })} style={{ ...input, padding: '2px 3px', width: 96 }} />
                                    <input type="time" value={v.end_time || ''} onChange={(e) => set(p.key, d, { end_time: e.target.value, off: false })} style={{ ...input, padding: '2px 3px', width: 96 }} />
                                  </>
                                )}
                                <span style={{ fontSize: 11 }}>
                                  <button type="button" onClick={() => set(p.key, d, { off: !v.off })} style={linkBtn}>{v.off ? 'working' : 'off'}</button>
                                  {(v.start_time || v.end_time || v.off) && <> · <button type="button" onClick={() => clear(p.key, d)} style={linkBtn}>clear</button></>}
                                </span>
                              </div>
                            ) : (v.off ? 'OFF' : v.start_time ? `${clockShort(v.start_time)}-${clockShort(v.end_time)}` : '')}
                          </td>
                        );
                      })}
                      {editable && t === 'cr' && (
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {CR_SHIFTS.map(([a, b], i) => <button key={a} type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11, marginLeft: 3 }} onClick={() => crShift(p, i)}>{clockShort(a)}-{clockShort(b)}</button>)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      <p style={muted}>Once a day is on the schedule, the grid can tell scheduled off from called out, and late and left early are measured against it. Blank days use the person's standard times.</p>
    </div>
  );
}

const linkBtn = { background: 'none', border: 0, color: '#4b5563', textDecoration: 'underline', cursor: 'pointer', fontSize: 11, padding: 0 };
const cellText = (v) => (!v ? '' : v.off ? 'OFF' : v.start_time ? `${clockShort(v.start_time)}-${clockShort(v.end_time)}` : '');

// ── Print: a clean landscape page for the board ─────────────────────────────
function printWeek(teamName, people, days, rows, holidays) {
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const head = days.map((d) => `<th${holidays.includes(d) ? ' class="hol"' : ''}>${fmtDow(d)}<br>${fmtMd(d)}</th>`).join('');
  const body = people.map((p) => `<tr><td class="n">${esc(p.name)}</td>${days.map((d) => {
    const v = rows[key(p.key, d)];
    return `<td class="${v && v.off ? 'off' : ''}">${esc(cellText(v))}</td>`;
  }).join('')}</tr>`).join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${esc(teamName)} schedule, week of ${esc(fmtDay(days[0]))}</title><style>
    @page { size: landscape; margin: 0.4in; }
    body { font-family: Montserrat, Arial, sans-serif; color: #111; }
    h1 { font-size: 26px; margin: 0 0 4px; color: #003b5c; } p { margin: 0 0 14px; font-size: 14px; }
    table { border-collapse: collapse; width: 100%; } th, td { border: 2px solid #333; padding: 10px 6px; text-align: center; font-size: 20px; }
    th { background: #003b5c; color: #fff; font-size: 16px; } th.hol { background: #b42318; }
    td.n { text-align: left; font-weight: 700; white-space: nowrap; } td.off { background: #e5e7eb; font-weight: 700; color: #555; }
  </style></head><body><h1>${esc(teamName)} schedule</h1><p>Week of ${esc(fmtDay(days[0]))} to ${esc(fmtDay(days[6]))}</p>
  <table><tr><th></th>${head}</tr>${body}</table><script>window.onload = () => window.print();</script></body></html>`);
  w.document.close();
}

// ── Share picture: draw the week, then the phone's share sheet ───────────────
async function shareWeek(teamName, people, days, rows, holidays) {
  const scale = 2;
  const nameW = 190; const colW = 112; const rowH = 46; const headH = 112;
  const W = nameW + colW * 7 + 20; const H = headH + rowH * (people.length + 1) + 20;
  const c = document.createElement('canvas');
  c.width = W * scale; c.height = H * scale;
  const g = c.getContext('2d');
  g.scale(scale, scale);
  g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
  g.fillStyle = '#003b5c'; g.font = 'bold 26px Montserrat, Arial, sans-serif'; g.fillText(`${teamName} schedule`, 10, 36);
  g.fillStyle = '#374151'; g.font = '16px Montserrat, Arial, sans-serif'; g.fillText(`Week of ${fmtDay(days[0])}`, 10, 62);
  const top = headH - 30;
  days.forEach((d, i) => {
    const x = nameW + i * colW;
    g.fillStyle = holidays.includes(d) ? '#b42318' : '#003b5c'; g.fillRect(x, top, colW, rowH);
    g.fillStyle = '#fff'; g.font = 'bold 15px Montserrat, Arial, sans-serif'; g.textAlign = 'center';
    g.fillText(fmtDow(d), x + colW / 2, top + 20); g.font = '14px Montserrat, Arial, sans-serif'; g.fillText(fmtMd(d), x + colW / 2, top + 38);
  });
  people.forEach((p, r) => {
    const y = top + rowH * (r + 1);
    g.fillStyle = r % 2 ? '#f9fafb' : '#fff'; g.fillRect(0, y, W, rowH);
    g.fillStyle = '#111'; g.textAlign = 'left'; g.font = 'bold 16px Montserrat, Arial, sans-serif'; g.fillText(p.name, 10, y + 29);
    days.forEach((d, i) => {
      const v = rows[key(p.key, d)];
      const x = nameW + i * colW;
      if (v && v.off) { g.fillStyle = '#e5e7eb'; g.fillRect(x + 1, y + 1, colW - 2, rowH - 2); }
      g.fillStyle = v && v.off ? '#555' : '#111'; g.textAlign = 'center'; g.font = `${v && v.off ? 'bold ' : ''}16px Montserrat, Arial, sans-serif`;
      g.fillText(cellText(v), x + colW / 2, y + 29);
    });
    g.strokeStyle = '#d1d5db'; g.beginPath(); g.moveTo(0, y + rowH); g.lineTo(W, y + rowH); g.stroke();
  });
  const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
  const file = new File([blob], `${teamName.replace(/\s+/g, '-')}-schedule-${days[0]}.png`, { type: 'image/png' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title: `${teamName} schedule` }); } catch (e) { if (e && e.name !== 'AbortError') throw e; }
    return;
  }
  // A computer with no share sheet: download the picture instead.
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
